import * as React from "react";
import {
  Box,
  CircularProgress,
  FormControl,
  TextField,
  Grid,
  IconButton,
  InputLabel,
  Typography,
  MenuItem,
  Autocomplete,
  Select,
  Switch,
} from "@mui/material";
import { getDatesByMonthAndYear } from "utils/helpers";
import { styled } from "@mui/material";
import { format } from "date-fns";
import Tabs from "@mui/material/Tabs";
import MuiButton from "@mui/material/Button";
import Tab from "@mui/material/Tab";
import { TextCard } from "components/TextCard/TextCard";
import { useAppDispatch, useAppSelector } from "hooks/reduxHooks";
import { selectCurrentClinicPatientsCount } from "store/PatientStore/selectors";
import ClearIcon from "@mui/icons-material/Clear";
import {
  selectClinicStats,
  selectCurrentClinician,
  selectCurrentClinicianLoading,
} from "store/ClinicianStore/selectors";
import { TABLE_HEADER_BY_HOME_TAB } from "../../../constants/dashboard";
import TableHeaderContainer from "../../ClinicOverview/components/TableHeaderContainer";
import StyledHome from "./Home.style";
import ReactDatePicker from "react-datepicker";
import { useEffect } from "react";
import { getClinicianPatientStats } from "../../../store/ClinicianStore/actions";
import { ButtonColorScheme, ButtonType } from "../../../types/ui";
import { Button } from "components/Buttons";
import { getPatientOverView } from "repositories/patientApi";
import DownloadCsvModal from "./DownloadCsvModal";
import { getElegibleEvents } from "repositories/elegibleEventsApi";
import { icons } from "constants/icons";
import { MONTH_HISTORIC_OPTIONS, MONTHS_NAMES } from "constants/dates";
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3, pr: 1 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

const StyledTab = styled(Tab)({
  "&.MuiButtonBase-root": {
    backgroundColor: "#F0F0F0",
    textTransform: "none",
    borderRadius: 10,
    fontSize: 14,
    margin: 10,
    padding: 10,
    fontWeight: "400",
  },
  "&.Mui-selected": {
    color: "white",
    backgroundColor: "#161728",
    textTransform: "none",
    borderRadius: 10,
    fontSize: 14,
    padding: 10,
    margin: 10,
    fontWeight: "400",
  },
});

export const Home = () => {
  const dispatch = useAppDispatch();
  const currentClinicPatientsCount: number = useAppSelector(
    selectCurrentClinicPatientsCount
  );
  const [activePatients, setActivePatients] = React.useState(0);
  const [showOnlyActivePatients, setShowOnlyActivePatients] =
    React.useState(false);
  const isLoading: boolean = useAppSelector(selectCurrentClinicianLoading);
  const currentClinician = useAppSelector(selectCurrentClinician);
  const currentClinicStats: any = useAppSelector(selectClinicStats);
  const [clinicStats, setClinicStats] = React.useState(currentClinicStats);
  const [openDate, setOpenDate] = React.useState(false);
  const cliniciansSelectList = currentClinicStats?.map((el: any) => ({
    label: el?.profileInfo?.firstName + " " + el?.profileInfo?.lastName,
    value: el?.clinicianId,
  }));
  const [selectedClinician, setSelectedClinician] = React.useState(null);
  const [patientSort, setPatientSort] = React.useState("DESC");
  const [openCsvModal, setOpenCsvModal] = React.useState(false);
  const [endDate, setEndDate] = React.useState<Date>(new Date());
  const [patients, setPatients] = React.useState([]);
  const [elegibleEvents, setElegibleEvents] = React.useState([]);
  const [clinicianHideNoPatient, setClinicianHideNoPatient] =
    React.useState<boolean>(false);
  const [selectedTab, setSelectedTab] = React.useState(0);
  const [elegibleEventFilters, setElegibleEventFilters] = React.useState({
    patientName: "",
    month: new Date().getMonth(),
    year: new Date().getFullYear(),
    dateLabel: "",
    update: false,
  });
  const [startDate, setStartDate] = React.useState<Date>(
    new Date(endDate?.getFullYear(), endDate?.getMonth(), 1)
  );
  const getClinicianName = (clinician: any) => {
    if (clinician.profileInfo)
      return `${clinician?.profileInfo?.firstName} ${clinician?.profileInfo?.lastName}`;
    else return "Clinician has no name set";
  };

  useEffect(() => {
    getStats();
  }, [clinicianHideNoPatient, elegibleEventFilters.update]);
  useEffect(() => {
    if (currentClinicStats) {
      const filtered = currentClinicStats?.filter(
        (item: any) => item.clinicianId === selectedClinician
      );
      setClinicStats(selectedClinician ? filtered : currentClinicStats);
    }
  }, [currentClinicStats, selectedClinician]);

  const onChangeDate = (dates: any) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const getStats = async () => {
    dispatch(
      getClinicianPatientStats(
        currentClinician?.clinicId,
        {
          startDate: new Date(startDate).toISOString(),
          endDate: new Date(endDate).toISOString(),
          sort: patientSort,
          hideNoPatient: clinicianHideNoPatient,
        },
        setActivePatients
      )
    );
    const patients = await getPatientOverView();
    const { selectedStartDate, selectedEndDate } = getDatesByMonthAndYear(
      Number(elegibleEventFilters.month),
      Number(elegibleEventFilters.year)
    );
    const elegibleEvents = await getElegibleEvents(
      selectedEndDate,
      selectedStartDate,
      elegibleEventFilters.patientName
    );
    setElegibleEvents(elegibleEvents);
    setPatients(patients);
    setOpenDate(false);
  };

  const getFilteredPatients = () => {
    if (patients && showOnlyActivePatients) {
      return patients.filter((patient: any) => patient.active);
    }
    return patients || [];
  };

  function getNextBillingDate(nextBillingDate: any) {
    if (!nextBillingDate || nextBillingDate === "No Date") return "No Date";
    return format(new Date(nextBillingDate), "PP");
  }

  const renderClinicians = (
    <div
      style={
        isLoading ? { display: "none" } : { width: "100%", marginLeft: "-15px" }
      }
    >
      {clinicStats?.map((clinician: any, index: any) => (
        <div
          key={clinician.clinicianId + index}
          style={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
            borderRadius: "5px",
            padding: "8px",
            backgroundColor: "white",
            marginBottom: "10px",
          }}
        >
          <div style={{ width: "300px" }}>
            <p>{getClinicianName(clinician)}</p>
          </div>
          <div style={{ width: "150px" }}>
            <p>{clinician.patients}</p>
          </div>
          <div style={{ width: "150px" }}>
            <p>{clinician.exams.length}</p>
          </div>
          <div style={{ width: "150px" }}>
            <p>{clinician.surveysCompleted}</p>
          </div>
          <div style={{ width: "150px" }}>
            <p>{clinician.dailyCheckIns.length}</p>
          </div>
          {/*  <div style={{ width: "150px" }}>
            <p>ELEGIBLE EVENTS</p>
          </div> */}
        </div>
      ))}
    </div>
  );
  const renderPatients = (
    <div
      style={
        isLoading
          ? {
              display: "none",
            }
          : { width: "100%", marginLeft: "-15px" }
      }
    >
      {getFilteredPatients()?.map((patient: any, index: any) => (
        <div
          key={patient.id + index}
          style={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
            borderRadius: "5px",
            padding: "8px",
            backgroundColor: "white",
            marginBottom: "10px",
          }}
        >
          <div style={{ width: "300px" }}>
            <p>{patient.name}</p>
          </div>
          {/*  <div style={{ width: "150px" }}>
            <p>99</p>
          </div> */}
          <div style={{ width: "150px" }}>
            <p>
              {patient.clinicTeamLead?.profileInfo?.firstName}{" "}
              {patient.clinicTeamLead?.profileInfo?.lastName}
            </p>
          </div>
          <div style={{ width: "150px" }}>
            <p>{patient.activities}/16</p>
          </div>
          <div style={{ width: "150px" }}>
            <p>{getNextBillingDate(patient.nextBillingDate)}</p>
          </div>
        </div>
      ))}
    </div>
  );

  const renderElegibleEvents = (
    <div
      style={
        isLoading
          ? {
              display: "none",
            }
          : { width: "100%", marginLeft: "-15px" }
      }
    >
      {elegibleEvents?.map((event: any, index: any) => (
        <div
          key={event._id}
          style={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
            alignItems: "center",
            borderRadius: "5px",
            padding: "8px",
            backgroundColor: "white",
            marginBottom: "10px",
          }}
        >
          <div style={{ width: "300px" }}>
            <p>{event.patientName}</p>
          </div>

          <div style={{ width: "150px" }}>
            <p>{event.clinicianName} </p>
          </div>
          <div style={{ width: "150px" }}>
            <p>{event.loggedActivities || 0}/16</p>
          </div>
          <div style={{ width: "150px" }}>
            <p>{format(new Date(event.startDate), "PP")}</p>
          </div>
          <div style={{ width: "150px" }}>
            <p>{format(new Date(event.endDate), "PP")}</p>
          </div>
          <div style={{ width: "150px" }}>
            <img
              alt="status"
              style={{ marginLeft: "20px" }}
              src={
                event.status === "complete"
                  ? icons.clinicActiveStatus
                  : icons.clinicDisabledStatus
              }
            />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <StyledHome>
      <Grid container spacing={2}>
        <Grid
          xs={12}
          px={2}
          py={3}
          mb={1}
          sx={{ backgroundColor: "white", borderRadius: 3 }}
          container
          direction="row"
          alignItems="flex-start"
        >
          <TextCard
            title={"Clinic Name"}
            subTitle={currentClinician?.clinic?.name || ""}
          />
          <Box mx={3} />
          <TextCard
            title={"Number of Patients"}
            subTitle={currentClinicPatientsCount + ""}
          />
          <Box mx={3} />
          <TextCard
            title={"Number of Active Patients"}
            subTitle={activePatients?.toString() || "0"}
          />
          <Box mx={3} />
          <TextCard
            title={"Number of Clinic Users"}
            subTitle={currentClinicStats?.length}
          />
        </Grid>

        <Grid xs={12} sx={{}}>
          <Grid
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Tabs
                value={selectedTab}
                onChange={(e, v) => {
                  setSelectedTab(v);
                }}
                TabIndicatorProps={{
                  style: { backgroundColor: "transparent" },
                }}
              >
                <StyledTab disabled={false} label="Patients" />
                <StyledTab disabled={false} label="Clinicians" />
                <StyledTab disabled={false} label="Historic data" />

                <Box flex={1} />
              </Tabs>
            </Box>

            {cliniciansSelectList && (
              <div
                style={{ width: "100%", textAlign: "end", marginTop: "20px" }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginTop: "20px",
                    marginBottom: "20px",
                    gap: "1px",
                  }}
                >
                  {selectedTab === 2 && (
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <TextField
                        label="Search by name"
                        sx={{ mr: 2, p: 0 }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          if (!value) {
                            return;
                          }
                          setElegibleEventFilters({
                            ...elegibleEventFilters,
                            update: !elegibleEventFilters.update,
                          });
                        }}
                        onChange={(e) => {
                          setElegibleEventFilters({
                            ...elegibleEventFilters,
                            patientName: e.target.value,
                          });
                        }}
                        value={elegibleEventFilters.patientName}
                      />

                      <Autocomplete
                        disableClearable={true}
                        disablePortal
                        id="combo-box-demo"
                        getOptionDisabled={(option) => Boolean(option.disabled)}
                        options={MONTH_HISTORIC_OPTIONS}
                        sx={{ width: 300 }}
                        onChange={(e, value: any) => {
                          const shouldUpdate =
                            elegibleEventFilters.month !== value?.month;
                          const payload = {
                            ...elegibleEventFilters,
                            month: value?.month,
                            year: value?.year,
                            dateLabel: value?.label,
                          };
                          setElegibleEventFilters(
                            shouldUpdate
                              ? {
                                  ...payload,
                                  update: !elegibleEventFilters.update,
                                }
                              : payload
                          );
                        }}
                        defaultValue={{
                          label: `${
                            MONTHS_NAMES[elegibleEventFilters.month]
                          }-${elegibleEventFilters.year.toString()}`,
                          disabled: false,
                          month: elegibleEventFilters.month.toString(),
                          year: elegibleEventFilters.year.toString(),
                        }}
                        renderInput={(params) => (
                          <TextField {...params} label="Selected period" />
                        )}
                      />

                      <MuiButton
                        sx={{ ml: 2 }}
                        onClick={() => {
                          setOpenCsvModal(true);
                        }}
                      >
                        Download as csv
                      </MuiButton>
                      <DownloadCsvModal
                        isOpen={openCsvModal}
                        setIsOpen={setOpenCsvModal}
                      />
                    </div>
                  )}
                  {selectedTab === 1 && (
                    <>
                      <div>
                        <ReactDatePicker
                          placeholderText="Sort by date"
                          selected={startDate}
                          onChange={onChangeDate}
                          startDate={startDate}
                          endDate={endDate}
                          selectsRange
                          onClickOutside={() => {
                            setOpenDate(true);
                          }}
                          open={openDate}
                          onInputClick={() => {
                            setOpenDate(true);
                          }}
                          shouldCloseOnSelect={false}
                          isClearable={true}
                        >
                          <div>
                            <Button
                              style={{ margin: "10px" }}
                              title="Apply"
                              onClick={() => {
                                getStats();
                              }}
                              type={ButtonType.small}
                              colorScheme={ButtonColorScheme.gradient}
                            />
                          </div>
                        </ReactDatePicker>
                      </div>
                      <FormControl
                        sx={{ m: 0, minWidth: 200, ml: 1 }}
                        size="small"
                      >
                        <InputLabel id="demo-select-small-label">
                          Sort by clinician
                        </InputLabel>
                        <Select
                          labelId="demo-select-small-label"
                          id="demo-select-small"
                          value={selectedClinician}
                          MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                          onChange={(e: any) =>
                            setSelectedClinician(e?.target?.value)
                          }
                          endAdornment={
                            <IconButton
                              sx={{
                                display: selectedClinician ? "" : "none",
                                marginRight: 1,
                              }}
                              onClick={() => setSelectedClinician(null)}
                            >
                              <ClearIcon />
                            </IconButton>
                          }
                        >
                          {cliniciansSelectList?.map((el: any) => (
                            <MenuItem key={el.value} value={el.value}>
                              {el.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>{" "}
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <Switch
                          color="success"
                          checked={clinicianHideNoPatient}
                          onChange={() => {
                            setClinicianHideNoPatient(!clinicianHideNoPatient);
                          }}
                        />
                        <Typography>Hide no patients</Typography>
                      </div>
                    </>
                  )}
                  {selectedTab === 0 && (
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <Switch
                        color="success"
                        checked={showOnlyActivePatients}
                        onChange={() => {
                          setShowOnlyActivePatients(!showOnlyActivePatients);
                        }}
                      />
                      <Typography>Show only active patients</Typography>
                    </div>
                  )}
                </div>
              </div>
            )}
          </Grid>

          <TableHeaderContainer
            headerList={TABLE_HEADER_BY_HOME_TAB[selectedTab]}
            onClick={() => {}}
            style={{ justifyContent: "space-between", marginTop: "30px" }}
          />
          {isLoading && (
            <div
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginTop: "40px",
                color: "black",
              }}
            >
              <CircularProgress />
            </div>
          )}
          {!clinicStats || clinicStats?.length === 0 ? (
            <div
              style={{
                marginTop: "5%",
                width: "100%",
                textAlign: "center",
                color: "#999",
              }}
            >
              No data available
            </div>
          ) : (
            <Grid xs={12}>
              {selectedTab === 0 && (
                <TabPanel value={0} index={0}>
                  {renderPatients}
                </TabPanel>
              )}
              {selectedTab === 1 && (
                <TabPanel value={1} index={1}>
                  {renderClinicians}
                </TabPanel>
              )}
              {selectedTab === 2 && (
                <TabPanel value={1} index={1}>
                  {renderElegibleEvents}
                </TabPanel>
              )}
            </Grid>
          )}
        </Grid>
      </Grid>
    </StyledHome>
  );
};
